"use strict";
/**
 * Shared group-chat API client, used by GroupThread + any app embedding it (mango-chat,
 * Social Circle). Moved verbatim from mango-chat/src/services/api.ts's groupsApi/
 * giftRegistryApi/eventsApi/restaurantsApi/businessesApi/storageApi (same endpoints, same
 * request shapes) - only the receiver changed (factory closure instead of module-level
 * constants), so behavior is unchanged for existing callers.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.createChatApi = void 0;
function createChatApi(params) {
    const API_BASE_URL = params.baseUrl;
    const getAuthHeaders = params.getAuthHeaders;
    const groups = {
        mine: async () => {
            const response = await fetch(`${API_BASE_URL}/groups/mine`, { headers: getAuthHeaders() });
            return response.json();
        },
        startDm: async (friendUserId) => {
            const response = await fetch(`${API_BASE_URL}/groups/dm/${friendUserId}`, {
                method: 'POST',
                headers: getAuthHeaders(),
            });
            return response.json();
        },
        getMembers: async (groupId) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/members`, { headers: getAuthHeaders() });
            return response.json();
        },
        create: async (name, inviteMangoIds) => {
            const response = await fetch(`${API_BASE_URL}/groups`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ name, type: 'personal', isPublic: false, inviteMangoIds }),
            });
            return response.json();
        },
        getMessages: async (groupId, channelId, page = 1) => {
            const searchParams = new URLSearchParams({ page: String(page) });
            if (channelId)
                searchParams.set('channelId', channelId);
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/messages?${searchParams.toString()}`, {
                headers: getAuthHeaders(),
            });
            return response.json();
        },
        sendMessage: async (groupId, content, options) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/messages`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ content, ...options }),
            });
            return response.json();
        },
        claim: async (groupId, messageId) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/messages/${messageId}/claim`, {
                method: 'POST',
                headers: getAuthHeaders(),
            });
            return response.json();
        },
        unclaim: async (groupId, messageId) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/messages/${messageId}/unclaim`, {
                method: 'POST',
                headers: getAuthHeaders(),
            });
            return response.json();
        },
        respondRsvp: async (groupId, messageId, response_) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/messages/${messageId}/rsvp`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ response: response_ }),
            });
            return response.json();
        },
        createPoll: async (groupId, question, options) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/polls`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ question, options }),
            });
            return response.json();
        },
        votePoll: async (groupId, pollId, optionIndex) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/polls/${pollId}/vote`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ optionIndex }),
            });
            return response.json();
        },
        toggleTask: async (groupId, messageId, done) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/messages/${messageId}/task`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ done }),
            });
            return response.json();
        },
        joinRide: async (groupId, messageId) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/messages/${messageId}/ride/join`, {
                method: 'POST',
                headers: getAuthHeaders(),
            });
            return response.json();
        },
        leaveRide: async (groupId, messageId) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/messages/${messageId}/ride/leave`, {
                method: 'POST',
                headers: getAuthHeaders(),
            });
            return response.json();
        },
        claimChecklistItem: async (groupId, messageId, itemId) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/messages/${messageId}/checklist/${itemId}/claim`, {
                method: 'POST',
                headers: getAuthHeaders(),
            });
            return response.json();
        },
        unclaimChecklistItem: async (groupId, messageId, itemId) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/messages/${messageId}/checklist/${itemId}/unclaim`, {
                method: 'POST',
                headers: getAuthHeaders(),
            });
            return response.json();
        },
        eventRsvp: async (groupId, messageId) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/messages/${messageId}/event/rsvp`, {
                method: 'POST',
                headers: getAuthHeaders(),
            });
            return response.json();
        },
        eventUnrsvp: async (groupId, messageId) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/messages/${messageId}/event/unrsvp`, {
                method: 'POST',
                headers: getAuthHeaders(),
            });
            return response.json();
        },
        pendingEventInvites: async () => {
            const response = await fetch(`${API_BASE_URL}/groups/my/pending-event-invites`, {
                headers: getAuthHeaders(),
            });
            return response.json();
        },
        createExpense: async (groupId, title, totalAmount, shares, dueDate) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/expenses`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ title, totalAmount, shares, dueDate }),
            });
            return response.json();
        },
        payExpenseShare: async (groupId, messageId) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/messages/${messageId}/expense/pay`, {
                method: 'POST',
                headers: getAuthHeaders(),
            });
            return response.json();
        },
        markExpenseShareExternal: async (groupId, messageId) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/messages/${messageId}/expense/mark-external`, {
                method: 'POST',
                headers: getAuthHeaders(),
            });
            return response.json();
        },
        translateMessage: async (groupId, messageId, targetLanguage) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/messages/${messageId}/translate`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ targetLanguage }),
            });
            return response.json();
        },
        transcribeMessage: async (groupId, messageId) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/messages/${messageId}/transcribe`, {
                method: 'POST',
                headers: getAuthHeaders(),
            });
            return response.json();
        },
        rsvpGroupEvent: async (groupId, eventId) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/events/${eventId}/rsvp`, { method: 'POST', headers: getAuthHeaders() });
            return response.json();
        },
        unrsvpGroupEvent: async (groupId, eventId) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/events/${eventId}/unrsvp`, { method: 'POST', headers: getAuthHeaders() });
            return response.json();
        },
        rsvpEventShare: async (groupId, messageId) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/messages/${messageId}/event/rsvp`, { method: 'POST', headers: getAuthHeaders() });
            return response.json();
        },
        getLinkPreview: async (groupId, url) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/link-preview`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ url }),
            });
            return response.json();
        },
        createFindATime: async (groupId, title, durationMinutes, slots) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/find-a-time`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ title, durationMinutes, slots }),
            });
            return response.json();
        },
        suggestFindATimeSlots: async (groupId, durationMinutes) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/find-a-time/suggest-slots`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ durationMinutes }),
            });
            return response.json();
        },
        voteFindATime: async (groupId, messageId, slotIndex) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/messages/${messageId}/find-a-time/vote`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ slotIndex }),
            });
            return response.json();
        },
        confirmFindATime: async (groupId, messageId, slotIndex) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/messages/${messageId}/find-a-time/confirm`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ slotIndex }),
            });
            return response.json();
        },
        toggleReaction: async (groupId, messageId, emoji) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/messages/${messageId}/reactions`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ emoji }),
            });
            return response.json();
        },
        getComments: async (groupId, messageId) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/messages/${messageId}/comments`, {
                headers: getAuthHeaders(),
            });
            return response.json();
        },
        addComment: async (groupId, messageId, content) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/messages/${messageId}/comments`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ content }),
            });
            return response.json();
        },
        toggleCommentReaction: async (groupId, messageId, commentId, emoji) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/messages/${messageId}/comments/${commentId}/reactions`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ emoji }),
            });
            return response.json();
        },
        createDinnerPlan: async (groupId, dinnerParams) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/dinner-plans`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(dinnerParams),
            });
            return response.json();
        },
        confirmMeetup: async (groupId, messageId, meetupParams) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/messages/${messageId}/meetup-confirm`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(meetupParams),
            });
            return response.json();
        },
        createGroupPurchase: async (groupId, purchaseParams) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/group-purchases`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(purchaseParams),
            });
            return response.json();
        },
        confirmGroupPurchase: async (groupId, messageId) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/messages/${messageId}/group-purchase/confirm`, {
                method: 'POST',
                headers: getAuthHeaders(),
            });
            return response.json();
        },
        declineGroupPurchase: async (groupId, messageId) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/messages/${messageId}/group-purchase/decline`, {
                method: 'POST',
                headers: getAuthHeaders(),
            });
            return response.json();
        },
    };
    const manage = {
        // Scheduling (Phase 5a)
        getAvailabilitySettings: async (groupId) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/manage/availability-settings`, { headers: getAuthHeaders() });
            return response.json();
        },
        setAvailabilitySettings: async (groupId, data) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/manage/availability-settings`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify(data),
            });
            return response.json();
        },
        getAvailability: async (groupId, date) => {
            const searchParams = new URLSearchParams({ date });
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/manage/availability?${searchParams.toString()}`, {
                headers: getAuthHeaders(),
            });
            return response.json();
        },
        getBlockedTimes: async (groupId) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/manage/blocked-times`, { headers: getAuthHeaders() });
            return response.json();
        },
        createBlockedTime: async (groupId, data) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/manage/blocked-times`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(data),
            });
            return response.json();
        },
        deleteBlockedTime: async (groupId, blockedTimeId) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/manage/blocked-times/${blockedTimeId}`, {
                method: 'DELETE',
                headers: getAuthHeaders(),
            });
            return response.json();
        },
        // Budget (Phase 5b)
        getExpenses: async (groupId) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/expenses`, { headers: getAuthHeaders() });
            return response.json();
        },
        getGroupPurchases: async (groupId) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/group-purchases`, { headers: getAuthHeaders() });
            return response.json();
        },
        // Tasks (Phase 5c)
        getTasks: async (groupId) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/tasks`, { headers: getAuthHeaders() });
            return response.json();
        },
        createTask: async (groupId, data) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/tasks`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(data),
            });
            return response.json();
        },
        updateTask: async (groupId, taskId, data) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/tasks/${taskId}`, {
                method: 'PATCH',
                headers: getAuthHeaders(),
                body: JSON.stringify(data),
            });
            return response.json();
        },
        deleteTask: async (groupId, taskId) => {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/tasks/${taskId}`, {
                method: 'DELETE',
                headers: getAuthHeaders(),
            });
            return response.json();
        },
    };
    const giftRegistry = {
        mine: async () => {
            const response = await fetch(`${API_BASE_URL}/gift-registry`, { headers: getAuthHeaders() });
            return response.json();
        },
    };
    const events = {
        publicList: async (search) => {
            const searchParams = new URLSearchParams({ limit: '20' });
            const response = await fetch(`${API_BASE_URL}/events/public?${searchParams.toString()}`, {
                headers: getAuthHeaders(),
            });
            const json = await response.json();
            const list = Array.isArray(json?.events) ? json.events : [];
            const q = search?.trim().toLowerCase();
            return q ? list.filter((e) => e.name?.toLowerCase().includes(q)) : list;
        },
    };
    const restaurants = {
        search: async (query) => {
            const searchParams = new URLSearchParams({ limit: '20', hasTableBooking: 'true' });
            if (query?.trim())
                searchParams.set('search', query.trim());
            const response = await fetch(`${API_BASE_URL}/business/public?${searchParams.toString()}`, {
                headers: getAuthHeaders(),
            });
            const json = await response.json();
            return Array.isArray(json?.data) ? json.data : [];
        },
        getTables: async (businessId) => {
            const response = await fetch(`${API_BASE_URL}/table-booking/public/${businessId}/resources`, {
                headers: getAuthHeaders(),
            });
            const json = await response.json();
            return Array.isArray(json?.data) ? json.data : [];
        },
    };
    const businesses = {
        /** Any business, not just table-booking ones — used by the group-purchase composer. */
        search: async (query) => {
            const searchParams = new URLSearchParams({ limit: '20' });
            if (query?.trim())
                searchParams.set('search', query.trim());
            const response = await fetch(`${API_BASE_URL}/business/public?${searchParams.toString()}`, {
                headers: getAuthHeaders(),
            });
            const json = await response.json();
            return Array.isArray(json?.data) ? json.data : [];
        },
    };
    const storage = {
        /** Multipart upload — reuses the same /storage/upload-attachment endpoint already used
         *  elsewhere in this platform for product/event photos. No new backend work needed. */
        uploadAttachment: async (file) => {
            const token = params.getToken();
            const formData = new FormData();
            formData.append('file', file);
            const response = await fetch(`${API_BASE_URL}/storage/upload-attachment`, {
                method: 'POST',
                headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                body: formData,
            });
            return response.json();
        },
    };
    return { groups, manage, giftRegistry, events, restaurants, businesses, storage };
}
exports.createChatApi = createChatApi;
